import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format, parse } from 'date-fns'
import { Button } from '@/components/ui/button'
import { TimePicker } from '@/components/ui/TimePicker'
import { DatePicker } from '@/components/ui/DatePicker'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import type { Appointment } from '@/lib/api'

export interface RescheduleDialogProps {
  open: boolean
  appointment: Appointment | null
  /** 目标日期（拖拽落点 / 面板入口预填当前日期） */
  targetDate: Date | null
  targetStartTime: string
  targetEndTime: string
  onClose: () => void
  onConfirm: (appt: Appointment, date: Date, startTime: string, endTime: string) => void
  isLoading?: boolean
  /** OPT-028：冲突警告文案（有值时显示红色提醒） */
  warning?: string | null
}

function formatDateTime(epoch: number, time: string) {
  const d = new Date(epoch * 1000)
  return `${d.toLocaleDateString()} ${time}`
}

/**
 * 更改时间确认弹窗（OPT-013：直接生效，无需学员确认）。
 * 拖拽打开时预填落点时间；面板/移动端打开时预填当前时间，可再编辑。
 */
export function RescheduleDialog({
  open,
  appointment,
  targetDate,
  targetStartTime,
  targetEndTime,
  onClose,
  onConfirm,
  isLoading = false,
  warning,
}: RescheduleDialogProps) {
  const { t } = useTranslation()
  const [dateStr, setDateStr] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')

  // 打开/切换目标时同步预填值
  useEffect(() => {
    if (open && targetDate) {
      setDateStr(format(targetDate, 'yyyy-MM-dd'))
      setStartTime(targetStartTime)
      setEndTime(targetEndTime)
    }
  }, [open, targetDate, targetStartTime, targetEndTime])

  const parsedDate = dateStr ? parse(dateStr, 'yyyy-MM-dd', new Date()) : null
  const valid =
    !!appointment &&
    !!parsedDate &&
    !Number.isNaN(parsedDate.getTime()) &&
    /^\d{2}:\d{2}$/.test(startTime) &&
    /^\d{2}:\d{2}$/.test(endTime) &&
    endTime > startTime

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('schedule.reschedule.title')}</DialogTitle>
          <DialogDescription>{t('schedule.reschedule.hint')}</DialogDescription>
        </DialogHeader>
        {appointment && (
          <div className="space-y-3 py-2">
            <div className="text-sm">
              <span className="text-slate-500">{t('schedule.reschedule.oldTime')}: </span>
              {formatDateTime(appointment.scheduledDate, `${appointment.startTime} - ${appointment.endTime}`)}
            </div>
            <div className="space-y-2">
              <label className="block text-sm text-slate-500" htmlFor="reschedule-date">
                {t('schedule.reschedule.newTime')}
              </label>
              <DatePicker id="reschedule-date" value={dateStr} onChange={setDateStr} />
              <div className="flex items-center gap-2">
                <TimePicker id="reschedule-start" value={startTime} onChange={setStartTime} />
                <span className="text-slate-400">-</span>
                <TimePicker id="reschedule-end" value={endTime} onChange={setEndTime} />
              </div>
            </div>
          </div>
        )}
        {warning && <p className="text-sm text-red-600 mb-2 whitespace-pre-line">{warning}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!valid || isLoading}
            onClick={() => {
              if (appointment && parsedDate && valid) {
                onConfirm(appointment, parsedDate, startTime, endTime)
              }
            }}
          >
            {isLoading ? t('common.saving') : t('schedule.reschedule.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
